import React, { useId } from 'react';
import TimeSeriesChart, { CHART_COLORS } from './TimeSeriesChart';
import { formatPercent, formatRate } from './format';
import { diskKeys, labelDisk, percentSeries, rateSeries } from './series';
import type { Sample } from './types';

interface PanelProps {
  samples: Sample[];
  timestamps: number[];
  sampleCounts: number[];
  intervalSeconds: number;
  selectedDisk: string | null;
  onSelectDisk: (key: string) => void;
}

/**
 * One volume at a time, chosen from a dropdown.
 *
 * A processing box can mount a lot of filesystems - overlays, binds, tmpfs - and
 * stacking a pair of charts for each buries the one an admin came to look at
 * under dozens of others.
 */
export default function DisksPanel({
  samples,
  timestamps,
  sampleCounts,
  intervalSeconds,
  selectedDisk,
  onSelectDisk,
}: PanelProps) {
  const selectId = `${useId().replace(/:/g, '')}-disk`;

  const keys = diskKeys(samples);

  if (samples.length > 0 && keys.length === 0) {
    return <p className="system-stats__empty">No disks reported.</p>;
  }

  // The selection can go stale: a volume can drop out of the window between
  // polls, and the choice outlives the disk list that produced it.
  const active = selectedDisk && keys.includes(selectedDisk) ? selectedDisk : keys[0];

  if (!active) return null;

  const label = labelDisk(active);

  // A volume missing from an entry yields null, which breaks the line at that
  // point rather than pretending it dropped to zero.
  const usage = percentSeries(samples, (snapshot) => snapshot.disks[active]?.usage ?? null, {
    id: `${active}-usage`,
    label: 'Used',
    color: CHART_COLORS.primary,
  });

  const read = rateSeries(
    samples,
    (snapshot) => snapshot.disks[active]?.bytes_read ?? null,
    intervalSeconds,
    { id: `${active}-read`, label: 'Read', color: CHART_COLORS.read },
  );

  const written = rateSeries(
    samples,
    (snapshot) => snapshot.disks[active]?.bytes_written ?? null,
    intervalSeconds,
    { id: `${active}-write`, label: 'Written', color: CHART_COLORS.write },
  );

  return (
    <>
      <div className="system-stats__disk-picker">
        <label htmlFor={selectId}>Volume</label>
        <select id={selectId} value={active} onChange={(event) => onSelectDisk(event.target.value)}>
          {keys.map((key) => (
            <option key={key} value={key}>
              {labelDisk(key)}
            </option>
          ))}
        </select>
      </div>

      {/* Keyed on the volume so switching resets each chart's hover state
          rather than carrying a reading from one disk over to another. */}
      <TimeSeriesChart
        key={`${active}-usage`}
        title={`${label} — space used`}
        timestamps={timestamps}
        sampleCounts={sampleCounts}
        series={[usage]}
        yMax={100}
        formatValue={formatPercent}
      />

      <TimeSeriesChart
        key={`${active}-throughput`}
        title={`${label} — throughput`}
        timestamps={timestamps}
        sampleCounts={sampleCounts}
        series={[read, written]}
        formatValue={formatRate}
      />
    </>
  );
}
